import { PARAMETER_MAP } from "./PARAMETER_MAP";
const LAST_KNOWN = new Map();
// ======================================================
// UTIL: pega o último valor REAL de um parâmetro
// ======================================================
function getLatestByParam(messages, paramId) {
    return messages
        .filter(m => Number(m.parameter_id) === Number(paramId))
        .reduce((latest, cur) => {
            if (!latest) return cur;
            return cur.message_timestamp > latest.message_timestamp ? cur : latest;
        }, null)?.parameter_value ?? null;
}

// ======================================================
// Tradução oficial da categoria (números → texto do Excel)
// ======================================================
export function traduzCategoria(num) {
    return CATEGORY_MAP[num] ?? "UNKNOWN";
}

export const CATEGORY_MAP = {
    0: "UNSPECIFIED",
    1: "HEALTH_STATUS",
    2: "CRITICAL_ALARMS",
    3: "MAJOR_ALARMS",
    4: "OPERATIONAL_DATA",
    5: "GENERAL_INFORMATION",
};


// ======================================================
// DIREÇÃO DO TREM (bloco lógico)
// ======================================================
function resolveDirection(messages) {
    const forwardMsg = messages
        .filter(m => Number(m.parameter_id) === 17900756)
        .sort((a, b) => b.message_timestamp - a.message_timestamp)[0];

    const reverseMsg = messages
        .filter(m => Number(m.parameter_id) === 17900758)
        .sort((a, b) => b.message_timestamp - a.message_timestamp)[0];

    const candidates = [forwardMsg, reverseMsg].filter(Boolean);
    if (!candidates.length) return "Stopped";

    const latest = candidates.sort(
        (a, b) => b.message_timestamp - a.message_timestamp
    )[0];

    if (latest.parameter_id === 17900756 && latest.parameter_value === 1)
        return "Forward";

    if (latest.parameter_id === 17900758 && latest.parameter_value === 1)
        return "Reverse";

    return "Stopped";
}

// ======================================================
// PARSER DO TREM
// ======================================================
export function parseTrainData(messages) {
    if (!messages?.length) return null;

    // VELOCIDADE
    const speed = getLatestByParam(messages, 18317504); // EVR_ITrainSpeed

    // TENSÃO NA LINHA
    const tensionOnLine = getLatestByParam(messages, 17899616); // DD_CInVolt

    // CORRENTE TOTAL
    const currentConsumption = getLatestByParam(messages, 18153568); // PCE_IHVCurr

    // DIREÇÃO
    const direction = resolveDirection(messages);

    // ALARMES
    const alarms = messages
        .filter(m => {
            const cat = traduzCategoria(m.category);
            return cat === "CRITICAL_ALARMS" || cat === "MAJOR_ALARMS";
        })
        .map(m => {
            const info = PARAMETER_MAP[m.parameter_id] ?? {};
            return {
                parameter_id: m.parameter_id,
                code: info.code ?? `P${m.parameter_id}`,
                name: info.name ?? "Unknown",
                category: traduzCategoria(m.category),
                value: m.parameter_value,
                timestamp: m.message_timestamp,
            };
        });

    return {
        speed,
        tensionOnLine,
        currentConsumption,
        direction,
        alarms,
        manufacturer: "Unknown",
        model: "Unknown",
    };
}

// ======================================================
// HELPERS DE CARRO
// ======================================================
function extractCarSuffixFromParamId(paramId) {
    const cfg = PARAMETER_MAP[paramId];
    if (!cfg || typeof cfg.code !== "string") return null;

    const match = cfg.code.match(/_([A-D][12])$/);
    return match ? match[1] : null;
}

// ======================================================
// RESOLVE PORTAS (BLOCO REAL, POR CARRO)
// ======================================================
function resolveDoorStatus(msgs) {
    const doorParamIds = Object.entries(PARAMETER_MAP)
        .filter(([_, cfg]) =>
            typeof cfg.code === "string" &&
            cfg.code.startsWith("DD_CDoor")
        )
        .map(([id]) => Number(id));

    const latestByParam = new Map();

    msgs
        .filter(m => doorParamIds.includes(Number(m.parameter_id)))
        .forEach(m => {
            const prev = latestByParam.get(m.parameter_id);
            if (!prev || m.message_timestamp > prev.message_timestamp) {
                latestByParam.set(m.parameter_id, m);
            }
        });

    const values = Array.from(latestByParam.entries()).map(([paramId, msg]) => {
        LAST_KNOWN.set(paramId, msg.parameter_value);
        return msg.parameter_value;
    });

    // fallback: se nenhum chegou nesse batch
    if (!values.length) {
        const cached = Array.from(LAST_KNOWN.entries())
            .filter(([pid]) => doorParamIds.includes(pid))
            .map(([, value]) => value);

        if (!cached.length) return "Desconhecido";
        return cached.every(v => v === 1) ? "Fechada" : "Aberta";
    }


    if (!values.length) return "Desconhecido";

    // SEMÂNTICA REAL DO EXCEL:
    // 1 = Door Closed and Locked
    // 0 = NOT closed/locked
    if (values.every(v => v === 1)) return "Fechada";
    if (values.some(v => v === 0)) return "Aberta";

    return "Desconhecido";
}

// ======================================================
// PARSER DO CARRO
// ======================================================
export function parseCarData(messages, carId) {
    const msgs = messages.filter(m => Number(m.carro) === Number(carId));
    if (!msgs.length) return null;

    // Descobre suffixes válidos para o carro
    const carSuffixes = new Set(
        msgs
            .map(m => extractCarSuffixFromParamId(m.parameter_id))
            .filter(Boolean)
    );

    // =========================
    // TEMPERATURA (média)
    // =========================
    const tempParamIds = Object.entries(PARAMETER_MAP)
        .filter(([id, cfg]) =>
            typeof cfg.code === "string" &&
            cfg.code.startsWith("DD_CVACTempIn_") &&
            carSuffixes.has(extractCarSuffixFromParamId(Number(id)))
        )
        .map(([id]) => Number(id));

    const tempValues = tempParamIds
        .map(id => getLatestByParam(msgs, id))
        .filter(v => typeof v === "number");

    const temperature = tempValues.length
        ? tempValues.reduce((a, b) => a + b, 0) / tempValues.length
        : null;

    // =========================
    // PASSAGEIROS (soma)
    // =========================
    const passParamIds = Object.entries(PARAMETER_MAP)
        .filter(([id, cfg]) =>
            typeof cfg.code === "string" &&
            cfg.code.startsWith("DD_CPassNum_") &&
            carSuffixes.has(extractCarSuffixFromParamId(Number(id)))
        )
        .map(([id]) => Number(id));

    const passValues = passParamIds
        .map(id => getLatestByParam(msgs, id))
        .filter(v => typeof v === "number");

    const passengers = passValues.length
        ? passValues.reduce((a, b) => a + b, 0)
        : null;

    // =========================
    // PORTAS (estado agregado)
    // =========================
    const doorStatus = resolveDoorStatus(msgs);

    return {
        name: `Carro ${carId}`,
        temperature,
        passengers,
        doorStatus
    };
}

// ======================================================
// RAW → DISPLAY TABLE
// ======================================================
export function toDisplayTable(messages) {
    return messages.map(m => {
        const info = PARAMETER_MAP[m.parameter_id] ?? {};

        return {
            id: m.id,
            timestamp: m.message_timestamp,
            carro: m.carro,
            parameter_id: m.parameter_id,
            value: m.parameter_value,
            batch_id: m.batch_id,
            publisher_id: m.publisher_id,

            raw_category: traduzCategoria(m.category),
            code: info.code ?? `P${m.parameter_id}`,
            description: info.name ?? "Unknown Parameter",
            type: info.type ?? "raw",
        };
    });
}
